/**
 * LeaderManager - Manages leader configuration and defaults.
 *
 * Tracks:
 * - Active leader style/configuration
 * - Arrow head size
 * - Line style
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { CommandHistory } from "../commands/CommandHistory";

/**
 * Leader arrow head style.
 */
export type LeaderArrowStyle = "filled" | "open" | "dot" | "none";

/**
 * Leader configuration options.
 */
export interface LeaderConfig {
  /** Arrow head style */
  arrowStyle: LeaderArrowStyle;
  /** Arrow head size (relative to text height) */
  arrowSizeRatio: number;
  /** Gap between text and first leader point (relative to text height) */
  textGapRatio: number;
  /** Gap between text and second leader point (relative to text height) */
  textGap2Ratio: number;
}

/** Default leader configuration */
const DEFAULT_CONFIG: LeaderConfig = {
  arrowStyle: "filled",
  arrowSizeRatio: 0.6,
  textGapRatio: 1,
  textGap2Ratio: 2,
};

/**
 * Manager for leader configuration.
 */
export class LeaderManager {
  private viewer: DRDrawingViewer;
  private history: CommandHistory;

  /** Active leader configuration */
  private _config: LeaderConfig = { ...DEFAULT_CONFIG };

  /** Change listeners */
  private changeListeners: Array<() => void> = [];

  constructor(viewer: DRDrawingViewer, history: CommandHistory) {
    this.viewer = viewer;
    this.history = history;
  }

  // ============== Configuration ==============

  /**
   * Get the current leader configuration.
   */
  get config(): LeaderConfig {
    return { ...this._config };
  }

  /**
   * Set the leader configuration.
   */
  setConfig(config: Partial<LeaderConfig>): void {
    this._config = { ...this._config, ...config };
    this.notifyChange();
  }

  /**
   * Get arrow style.
   */
  get arrowStyle(): LeaderArrowStyle {
    return this._config.arrowStyle;
  }

  /**
   * Set arrow style.
   */
  set arrowStyle(style: LeaderArrowStyle) {
    this._config.arrowStyle = style;
    this.notifyChange();
  }

  /**
   * Get arrow size ratio.
   */
  get arrowSizeRatio(): number {
    return this._config.arrowSizeRatio;
  }

  /**
   * Set arrow size ratio.
   */
  set arrowSizeRatio(ratio: number) {
    this._config.arrowSizeRatio = Math.max(0.1, Math.min(2, ratio));
    this.notifyChange();
  }

  // ============== Events ==============

  /**
   * Subscribe to changes.
   * @returns Unsubscribe function
   */
  onChange(handler: () => void): () => void {
    this.changeListeners.push(handler);
    return () => {
      const index = this.changeListeners.indexOf(handler);
      if (index >= 0) {
        this.changeListeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify change listeners.
   */
  private notifyChange(): void {
    for (const handler of this.changeListeners) {
      handler();
    }
  }

  // ============== Helpers ==============

  /**
   * Reset to default configuration.
   */
  reset(): void {
    this._config = { ...DEFAULT_CONFIG };
    this.notifyChange();
  }
}
